import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Package,
  Loader2,
  Truck,
  Check,
  X as XIcon,
  RefreshCw,
  ExternalLink,
  MapPin,
  CreditCard,
  Calendar,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface LineItem {
  id: number;
  product_id: number;
  variant_id: number;
  title: string;
  name: string;
  variant_title: string | null;
  price: string;
  quantity: number;
  sku: string | null;
  vendor: string | null;
  image_src: string | null;
}

interface Fulfillment {
  id: number;
  tracking_number: string | null;
  tracking_url: string | null;
  tracking_company: string | null;
  shipment_status: string | null;
  status: string | null;
  created_at: string;
  updated_at: string;
  events: any[];
}

interface Address {
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  province: string | null;
  country: string | null;
  zip: string | null;
}

interface Order {
  id: number;
  order_number: number;
  name: string;
  email: string | null;
  created_at: string;
  processed_at: string | null;
  cancelled_at: string | null;
  financial_status: string | null;
  fulfillment_status: string | null;
  currency: string;
  total_price: string;
  total_line_items_price: string;
  customer: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  billing_address: Address | null;
  shipping_address: Address | null;
  line_items: LineItem[];
  fulfillments: Fulfillment[];
  shipping_lines: Array<{
    id: number;
    title: string;
    price: string;
    code: string | null;
  }>;
  note: string | null;
  order_status_url: string | null;
  platform: string;
}

interface OrdersViewProps {
  organizationId: string;
  userInfo: {
    fullName?: string;
    email?: string;
    phone?: string;
  } | null;
  onBack: () => void;
  theme: "light" | "dark";
  // Cached orders passed from parent so UI doesn't flash between view switches.
  // Parent is authoritative — we hand new results back via onOrdersFetched.
  cachedOrders: Order[] | null;
  onOrdersFetched: (orders: Order[]) => void;
  // Loop Subscriptions (loopwork.co) is connected for this org — show the
  // visitor's subscriptions above their orders (read-only; changes go
  // through the chat / the store's AI agent).
  loopEnabled?: boolean;
  // FlowIQ's own subscription engine (Phyto-style) opted in for the widget —
  // same read-only section, served by customer-portal /widget-subscriptions.
  flowiqSubsEnabled?: boolean;
  // Opens the full in-widget subscription manager (ManageSubscriptionsView).
  onManage?: () => void;
}

// ---- Subscription shapes ----
// One shape, two sources: loop-subscriptions `widget_subscriptions` (numeric
// Loop ids) and customer-portal `/widget-subscriptions` (truncated FlowIQ ids,
// always ZAR). Both emit ACTIVE/PAUSED statuses and the same line layout.

interface WidgetSubLine {
  title: string | null;
  variant_title: string | null;
  quantity: number | null;
  price: string | number | null;
  is_one_time: boolean;
}

interface WidgetSub {
  subscription_id: number | string;
  status: string | null;
  next_billing_date: string | null;
  frequency: string;
  currency: string | null;
  items_total: number | string | null;
  /** FlowIQ discount-ladder subs: items_total is the subscriber price of the next
   *  delivery; this is the price before it and the percent off. */
  items_total_before_discount?: number | string | null;
  subscriber_discount_percent?: number | null;
  delivery_price?: number | string | null;
  lines: WidgetSubLine[];
  payment_method?: {
    card_brand: string | null;
    card_last4: string | null;
  };
  shipping_address?: {
    address1?: string | null;
    city: string | null;
  };
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  ZAR: "R",
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  NZD: "NZ$",
};

const fmtMoney = (amount: string | number, currency: string): string => {
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  if (Number.isNaN(n)) return `${currency} 0.00`;
  const symbol = CURRENCY_SYMBOLS[currency] || `${currency} `;
  return `${symbol}${n.toFixed(2)}`;
};

const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const StatusBadge: React.FC<{
  status: string | null;
  type: "financial" | "fulfillment";
}> = ({ status, type }) => {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-medium">
        {type === "financial" ? "Unpaid" : "Unfulfilled"}
      </span>
    );
  }

  const normalized = status.toLowerCase();
  let bg = "bg-gray-100";
  let fg = "text-gray-600";
  let Icon: React.ComponentType<{ size?: number; className?: string }> | null =
    null;

  if (type === "financial") {
    if (normalized === "paid") {
      bg = "bg-green-50";
      fg = "text-green-700";
      Icon = Check;
    } else if (normalized === "refunded" || normalized === "partially_refunded") {
      bg = "bg-amber-50";
      fg = "text-amber-700";
      Icon = RefreshCw;
    } else if (normalized === "voided") {
      bg = "bg-gray-100";
      fg = "text-gray-500";
      Icon = XIcon;
    } else if (normalized === "pending") {
      bg = "bg-blue-50";
      fg = "text-blue-700";
    }
  } else {
    if (normalized === "fulfilled") {
      bg = "bg-green-50";
      fg = "text-green-700";
      Icon = Check;
    } else if (normalized === "partial") {
      bg = "bg-blue-50";
      fg = "text-blue-700";
      Icon = Truck;
    } else if (normalized === "unfulfilled" || normalized === "null") {
      bg = "bg-gray-100";
      fg = "text-gray-600";
    }
  }

  const label = normalized
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${bg} ${fg} text-[10px] font-medium whitespace-nowrap`}
    >
      {Icon && <Icon size={10} />}
      {label}
    </span>
  );
};

const OrderCard: React.FC<{ order: Order }> = ({ order }) => {
  const address = order.shipping_address || order.billing_address;
  const addressLines = address
    ? [
        [address.first_name, address.last_name].filter(Boolean).join(" "),
        address.address1,
        address.address2,
        [address.city, address.province, address.zip]
          .filter(Boolean)
          .join(", "),
        address.country,
      ].filter((l) => l && l.trim().length > 0)
    : [];

  const subtotal = parseFloat(order.total_line_items_price || "0");
  const shipping = order.shipping_lines.reduce(
    (sum, l) => sum + parseFloat(l.price || "0"),
    0
  );
  const total = parseFloat(order.total_price || "0");

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header row: order number + date + total */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-semibold text-gray-900">
              {order.name}
            </span>
            {order.cancelled_at && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[10px] font-medium">
                <XIcon size={10} /> Cancelled
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
            <Calendar size={11} />
            {fmtDate(order.created_at)}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-sm font-semibold text-gray-900">
            {fmtMoney(total, order.currency)}
          </div>
          <div className="text-[10px] text-gray-400">
            {order.line_items.length} item{order.line_items.length !== 1 && "s"}
          </div>
        </div>
      </div>

      {/* Status badges */}
      <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-1.5 flex-wrap">
        <StatusBadge status={order.financial_status} type="financial" />
        <StatusBadge status={order.fulfillment_status} type="fulfillment" />
      </div>

      {/* Line items */}
      <div className="px-4 py-3 space-y-2.5">
        {order.line_items.map((item) => (
          <div key={item.id} className="flex items-start gap-3">
            <div className="relative w-12 h-12 flex-shrink-0">
              <div className="w-full h-full rounded-lg bg-gray-50 border border-gray-100 overflow-hidden">
                {item.image_src ? (
                  <img
                    src={item.image_src}
                    alt={item.title}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package size={16} className="text-gray-300" />
                  </div>
                )}
              </div>
              <span className="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] px-1 rounded-full bg-gray-900 text-white text-[9px] font-bold flex items-center justify-center shadow-sm ring-2 ring-white z-10">
                {item.quantity}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900 leading-tight truncate">
                {item.title}
              </p>
              {item.variant_title && item.variant_title !== "Default Title" && (
                <p className="text-[10px] text-gray-500 truncate mt-0.5">
                  {item.variant_title}
                </p>
              )}
              {item.sku && (
                <p className="text-[9px] text-gray-400 truncate mt-0.5">
                  SKU: {item.sku}
                </p>
              )}
            </div>
            <div className="text-xs font-medium text-gray-700 flex-shrink-0 tabular-nums">
              {fmtMoney(item.price, order.currency)}
            </div>
          </div>
        ))}
      </div>

      {/* Totals breakdown */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 space-y-1">
        <div className="flex justify-between text-[11px]">
          <span className="text-gray-500">Subtotal</span>
          <span className="text-gray-700 tabular-nums">
            {fmtMoney(subtotal, order.currency)}
          </span>
        </div>
        {shipping > 0 && (
          <div className="flex justify-between text-[11px]">
            <span className="text-gray-500">Shipping</span>
            <span className="text-gray-700 tabular-nums">
              {fmtMoney(shipping, order.currency)}
            </span>
          </div>
        )}
        <div className="flex justify-between text-xs font-semibold pt-1 border-t border-gray-200 mt-1">
          <span className="text-gray-900">Total</span>
          <span className="text-gray-900 tabular-nums">
            {fmtMoney(total, order.currency)}
          </span>
        </div>
      </div>

      {/* Tracking / Fulfillments */}
      {order.fulfillments.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-100 space-y-2">
          {order.fulfillments.map((f, idx) => (
            <div key={f.id} className="bg-blue-50/50 rounded-lg p-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <Truck size={12} className="text-blue-700" />
                <span className="text-[11px] font-semibold text-blue-900">
                  {order.fulfillments.length > 1
                    ? `Shipment ${idx + 1}`
                    : "Shipment"}
                </span>
                {f.shipment_status && (
                  <span className="text-[10px] text-blue-700">
                    · {f.shipment_status.replace(/_/g, " ")}
                  </span>
                )}
              </div>
              {f.tracking_company && (
                <p className="text-[10px] text-gray-600">{f.tracking_company}</p>
              )}
              {f.tracking_number && (
                <p className="text-[11px] font-mono text-gray-800 mt-0.5 break-all">
                  {f.tracking_number}
                </p>
              )}
              {f.tracking_url && (
                <a
                  href={f.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:underline mt-1 font-medium"
                >
                  Track package <ExternalLink size={10} />
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Shipping address */}
      {addressLines.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-100">
          <div className="flex items-start gap-1.5">
            <MapPin size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="text-[11px] text-gray-600 leading-relaxed">
              <div className="text-gray-500 font-medium mb-0.5">
                Shipping to
              </div>
              {addressLines.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Note */}
      {order.note && (
        <div className="px-4 py-3 border-t border-gray-100">
          <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">
            Note
          </div>
          <p className="text-[11px] text-gray-600 italic">{order.note}</p>
        </div>
      )}

      {/* View on Shopify */}
      {order.order_status_url && (
        <div className="px-4 py-3 border-t border-gray-100">
          <a
            href={order.order_status_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[11px] text-gray-700 hover:text-gray-900 font-medium"
          >
            <CreditCard size={11} /> View order status page
            <ExternalLink size={10} />
          </a>
        </div>
      )}
    </div>
  );
};

// One subscription card — deliberately read-only: management runs through the
// chat (the AI agent has the full subscription toolset), so the widget only
// informs. Renders both Loop and FlowIQ subscriptions (same shape).
const SubscriptionCard: React.FC<{ sub: WidgetSub }> = ({ sub }) => {
  const status = (sub.status || "").toUpperCase();
  const statusStyle =
    status === "ACTIVE"
      ? "bg-green-50 text-green-700"
      : status === "PAUSED"
        ? "bg-amber-50 text-amber-700"
        : "bg-gray-100 text-gray-500";
  const currency = sub.currency || "EUR";
  const recurring = sub.lines.filter((l) => !l.is_one_time);
  const oneTime = sub.lines.filter((l) => l.is_one_time);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <RefreshCw size={13} className="text-purple-600 flex-shrink-0" />
            <span className="text-sm font-semibold text-gray-900">
              Subscription
            </span>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full ${statusStyle} text-[10px] font-medium`}
            >
              {status.charAt(0) + status.slice(1).toLowerCase()}
            </span>
          </div>
          <div className="text-[11px] text-gray-500 capitalize">
            Delivered {sub.frequency}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-sm font-semibold text-gray-900">
            {fmtMoney(sub.items_total ?? 0, currency)}
          </div>
          <div className="text-[10px] text-gray-400">
            {sub.subscriber_discount_percent
              ? `next delivery · ${Number.isInteger(Number(sub.subscriber_discount_percent)) ? Number(sub.subscriber_discount_percent) : Number(sub.subscriber_discount_percent).toFixed(1).replace(/\.0$/, "")}% subscriber discount`
              : "per delivery"}
          </div>
        </div>
      </div>

      {sub.next_billing_date && status === "ACTIVE" && (
        <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-1.5 text-[11px] text-gray-600">
          <Calendar size={11} className="text-gray-400" />
          Next delivery billed on{" "}
          <span className="font-medium text-gray-800">
            {fmtDate(sub.next_billing_date)}
          </span>
        </div>
      )}

      <div className="px-4 py-3 space-y-2">
        {recurring.map((l, i) => (
          <div key={i} className="flex items-start justify-between gap-3">
            <p className="text-xs font-medium text-gray-900 leading-tight">
              <span className="text-gray-400 mr-1.5">{l.quantity}×</span>
              {l.title || "Item"}
              {l.variant_title && l.variant_title !== "Default Title" && (
                <span className="block text-[10px] text-gray-500 font-normal mt-0.5">
                  {l.variant_title}
                </span>
              )}
            </p>
            {l.price != null && (
              <span className="text-xs font-medium text-gray-700 flex-shrink-0 tabular-nums">
                {fmtMoney(l.price, currency)}
              </span>
            )}
          </div>
        ))}
        {oneTime.map((l, i) => (
          <div key={`ot-${i}`} className="flex items-start justify-between gap-3">
            <p className="text-xs font-medium text-gray-900 leading-tight">
              <span className="text-gray-400 mr-1.5">{l.quantity}×</span>
              {l.title || "Item"}
              <span className="ml-1.5 text-[9px] font-semibold text-blue-600 bg-blue-50 rounded px-1 py-0.5 align-middle">
                next order only
              </span>
            </p>
            {l.price != null && (
              <span className="text-xs font-medium text-gray-700 flex-shrink-0 tabular-nums">
                {fmtMoney(l.price, currency)}
              </span>
            )}
          </div>
        ))}
      </div>

      {(sub.payment_method?.card_last4 || sub.shipping_address?.city) && (
        <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/50 flex items-center gap-4 text-[10px] text-gray-500">
          {sub.payment_method?.card_last4 && (
            <span className="inline-flex items-center gap-1">
              <CreditCard size={10} /> •••• {sub.payment_method.card_last4}
            </span>
          )}
          {sub.shipping_address?.city && (
            <span className="inline-flex items-center gap-1 truncate">
              <MapPin size={10} /> {sub.shipping_address.city}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const OrdersView: React.FC<OrdersViewProps> = ({
  organizationId,
  userInfo,
  onBack,
  cachedOrders,
  onOrdersFetched,
  loopEnabled,
  flowiqSubsEnabled,
  onManage,
}) => {
  const [orders, setOrders] = useState<Order[] | null>(cachedOrders);
  const [isLoading, setIsLoading] = useState<boolean>(cachedOrders === null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(
    cachedOrders !== null
  );
  const [error, setError] = useState<string | null>(null);
  const subsEnabled = loopEnabled || flowiqSubsEnabled;
  // Subscriptions — null = not loaded yet; [] = loaded, none found.
  // Failures collapse to [] silently: the section simply doesn't render, and
  // the orders list (the view's main job) is never blocked by it.
  const [subs, setSubs] = useState<WidgetSub[] | null>(subsEnabled ? null : []);

  useEffect(() => {
    if (!subsEnabled) return;
    const email = userInfo?.email;
    const phone = userInfo?.phone;
    if (!email && !phone) {
      setSubs([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Two engines, one shape: Loop (loopwork.co, Barkyn) or FlowIQ's own
        // subscription engine (Phyto). An org runs one of them.
        const { data, error: fnError } = loopEnabled
          ? await supabase.functions.invoke("loop-subscriptions", {
              body: {
                action: "widget_subscriptions",
                organization_id: organizationId,
                email: email || undefined,
                phone: phone || undefined,
              },
            })
          : await supabase.functions.invoke(
              "customer-portal/widget-subscriptions",
              {
                body: {
                  organization_id: organizationId,
                  email: email || undefined,
                  phone: phone || undefined,
                },
              }
            );
        if (cancelled) return;
        const list: WidgetSub[] =
          !fnError && data?.success && Array.isArray(data.subscriptions)
            ? data.subscriptions
            : [];
        // Active + paused first; cancelled/expired stay hidden — a lapsed
        // subscriber opening My Orders shouldn't lead with dead subscriptions.
        setSubs(
          list.filter((s) => {
            const st = (s.status || "").toUpperCase();
            return st === "ACTIVE" || st === "PAUSED";
          })
        );
      } catch (e) {
        console.error("[OrdersView] subscriptions fetch error:", e);
        if (!cancelled) setSubs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subsEnabled, loopEnabled, organizationId]);

  const fetchOrders = async () => {
    const phone = userInfo?.phone;
    if (!phone) {
      setError("No phone number on file.");
      setIsLoading(false);
      setIsRefreshing(false);
      return;
    }

    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "customer-orders",
        {
          body: {
            organizationId,
            phoneNumber: phone,
          },
        }
      );

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      const fetched: Order[] = Array.isArray(data?.orders) ? data.orders : [];
      // Sort newest first
      fetched.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setOrders(fetched);
      onOrdersFetched(fetched);
      setError(null);
    } catch (e: any) {
      console.error("[OrdersView] fetch error:", e);
      // Only surface the error if there's no cached UI to fall back on
      if (!cachedOrders) {
        setError(e?.message || "Failed to load orders.");
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      className="relative h-full flex flex-col bg-gray-50"
      style={{ fontSize: "16px" }}
    >
      {/* Floating header */}
      <motion.div
        className="absolute top-3 left-3 right-3 z-20 flex items-center pointer-events-none"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
      >
        <motion.button
          onClick={onBack}
          className="pointer-events-auto h-9 w-9 rounded-full bg-white shadow-md ring-1 ring-black/5 flex items-center justify-center hover:bg-gray-50 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Back"
          aria-label="Back"
        >
          <ArrowLeft size={18} className="text-gray-700" />
        </motion.button>

        {/* Shifted left of the widget's permanent top-right control — one
            36px chip on both breakpoints (see ChatSession's header). */}
        <div className="pointer-events-auto ml-auto mr-11 flex items-center gap-1.5 h-9 px-3 rounded-full bg-white shadow-md ring-1 ring-black/5">
          <Package size={14} className="text-gray-600" />
          <span className="text-sm font-semibold text-gray-800">My Orders</span>
          {isRefreshing && (
            <Loader2 size={12} className="animate-spin text-gray-400 ml-0.5" />
          )}
        </div>
      </motion.div>

      {/* Body — overscroll-contain: never chain scroll to the page behind */}
      <div className="flex-1 overflow-y-auto overscroll-contain pt-16">
        {isLoading ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-gray-400">
            <Loader2 size={24} className="animate-spin" />
            <span className="text-xs">Loading your orders…</span>
          </div>
        ) : error && (!orders || orders.length === 0) ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
              <XIcon size={20} className="text-red-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800">
                Could not load orders
              </p>
              <p className="text-xs text-gray-500 mt-1">{error}</p>
            </div>
            <button
              onClick={() => {
                setIsLoading(true);
                setError(null);
                fetchOrders();
              }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 hover:text-gray-900 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw size={12} /> Try again
            </button>
          </div>
        ) : (!orders || orders.length === 0) && (!subs || subs.length === 0) ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
              <Package size={24} className="text-gray-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800">No orders yet</p>
              <p className="text-xs text-gray-500 mt-1">
                When you place an order it'll show up here.
              </p>
            </div>
          </div>
        ) : (
          <div className="p-3 space-y-3">
            {subs && subs.length > 0 && (
              <>
                <div className="px-1 flex items-center justify-between">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    {subs.length === 1 ? "My subscription" : "My subscriptions"}
                  </span>
                  {onManage && (
                    <button
                      type="button"
                      onClick={onManage}
                      className="text-[11px] font-semibold text-gray-700 hover:text-black underline underline-offset-2"
                    >
                      Manage
                    </button>
                  )}
                </div>
                {subs.map((sub) => (
                  <SubscriptionCard key={sub.subscription_id} sub={sub} />
                ))}
                <p className="px-1 text-[10px] text-gray-400">
                  {onManage ? (
                    <>Want to change, pause or move a delivery?{" "}
                    <button type="button" onClick={onManage} className="underline underline-offset-2 hover:text-gray-600">Manage your subscription</button>{" "}
                    — or just ask us in the chat.</>
                  ) : (
                    <>Want to change, pause or move a delivery? Just ask us in the chat — we can do it for you right here.</>
                  )}
                </p>
                {orders && orders.length > 0 && (
                  <div className="px-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    Order history
                  </div>
                )}
              </>
            )}
            {(orders ?? []).map((order) => (
              <OrderCard key={order.id} order={order} />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default OrdersView;
